import { memo, useEffect, useMemo, useState } from 'react';
import { Box, CloudConnection as DSCloudConnection } from '@capitalise/ui-design-system';
import { useAuthContext } from 'HOC/withAuth';
import { useHistory } from 'react-router-dom';
import { useSSEEvents } from '@capitalise/common';
import { useQueryClient } from 'react-query';
import { transformEventData } from 'services/sse/useSnapshotGeneration';
import {
  useMutationPostSnapshotGeneration,
  useQueryGetSnapshotLatestExperianData,
} from 'services/monitor/queries';
import { ICreditReportExperian } from 'modules/Business/shared/typesTS';
import { getYearsFromNow, toDistanceFromNowISO } from '@capitalise/utils';
import { useMutationDeleteIntegration } from 'services/auth/queries';
import { SectorTypeLabelShort } from 'enums/sector-type';
import { Analytics } from 'utils/Analytics';
import { AnalyticsEventAction, AnalyticsEventCategory } from 'enums/analytics-types';

type LatestSnapshot = ICreditReportExperian & { generatedAt: Date };

const CloudConnection = () => {
  const queryClient = useQueryClient();
  const { user: auth } = useAuthContext();
  const entityId = auth?.company?.company_id;
  const businessName = auth?.company?.name;
  const [lastEvent, setLastEvent] = useState({});
  const {
    data,
    isLoading,
    refetch,
  }: {
    data: {
      result: { data: { preview: { registrationDate: Date; sector: string } } };
      experianData: LatestSnapshot;
    };
    isLoading: boolean;
    refetch: () => void;
  } = useQueryGetSnapshotLatestExperianData(entityId);
  const { mutate: mutateDisconnect, isLoading: isLoadingDisconnect } =
    useMutationDeleteIntegration();
  const finished =
    lastEvent && Object.keys(lastEvent).length > 0 && lastEvent.percentage != 0
      ? lastEvent.percentage === 100
      : true;
  const generatedAt =
    data?.result?.data?.preview?.generatedAt &&
    new Date(data?.result?.data?.preview?.generatedAt).toISOString();
  const connectionStatus = data?.result?.data?.integrations?.connectionStatus;
  const { mutate } = useMutationPostSnapshotGeneration(entityId);
  const event = useSSEEvents('cloud-connection');
  const history = useHistory();
  const provider = data?.result?.data?.integrations?.privateAccounting;

  const age = data?.result?.data?.preview?.registrationDate
    ? getYearsFromNow(new Date(data?.result?.data?.preview?.registrationDate))
    : 0;
  useEffect(() => {
    const ev = transformEventData(event);
    setLastEvent(ev);
    if (!!ev?.syncRelatedCompanies) {
      queryClient.invalidateQueries('related-companies');
    }
    if (ev?.finished) {
      queryClient.invalidateQueries('snapshot-latest');
      refetch();
    }
  }, [event]);

  const handleSync = () => {
    mutate(entityId);
    setLastEvent({ percentage: 1 });
  };

  const handleDisconnect = () => {
    mutateDisconnect(
      { provider, entityId },
      {
        onSettled: () => {
          queryClient.invalidateQueries('snapshot-latest');
          refetch();
        },
      },
    );
  };

  const handleConnect = () => {
    Analytics.track(AnalyticsEventCategory.monitor, AnalyticsEventAction.connectInitiate);
    history.push('/business/cloud');
  };

  const [providerName, status] = useMemo(() => {
    if (connectionStatus === 'disconnected') {
      return [null, null];
    }
    return connectionStatus === 'errored'
      ? [provider, 'error']
      : [provider, generatedAt && toDistanceFromNowISO(generatedAt)];
  }, [provider, generatedAt]);

  return (
    <Box flexShrink={0}>
      <DSCloudConnection
        name={providerName}
        importing={isLoading || isLoadingDisconnect || !finished}
        onSync={handleSync}
        companyInformation={{
          name: businessName,
          sector: data?.result?.data?.preview?.sector
            ? SectorTypeLabelShort[data?.result?.data?.preview.sector]
            : '-',
          age: `${age} year${age > 1 ? 's' : ''}`,
        }}
        status={status}
        onConnect={handleConnect}
        onDisconnect={handleDisconnect}
      />
    </Box>
  );
};

CloudConnection.propTypes = {};

export default memo(CloudConnection, () => true);
